(() => {
  let productGrid = document.getElementById('ProductGrid');
  if (!productGrid) return;
  
  // currentPage semantics:
  // - On initial load: represents the page to reload (server-rendered page)
  // - After any load: incremented to represent "next page to load"
  // - After filters: set to displayedPage + 1 (since filter page is already displayed)
  let currentPage = parseInt(document.querySelector('.collection-products [data-current-page]')?.dataset.currentPage, 10);
  let productTotal = parseInt(document.querySelector('.collection-products [data-product-total]')?.dataset.productTotal, 10);
  let productLimit = parseInt(document.querySelector('.collection-products [data-product-limit]')?.dataset.productLimit, 10);
  let collectionSectionId = document.querySelector('.collection-products [data-collection-section-id]')?.dataset.collectionSectionId;
  let isLoading = false;  // Flag to track if a request is in progress
  let isFirstLoad = true; // Flag to track if the first load is happening
  let minLoadedPage = currentPage; // Track the minimum page number loaded
  let pageCache = {}; // Cache to store fetched pages for faster loading
  let isUserScrolling = false; // Track if user is actively scrolling
  let scrollTimeout = null; // Timeout for scroll tracking

  // Get current collection URL (without query params) to identify the collection
  const currentCollectionUrl = window.location.pathname.split('?')[0];
  
  // Check for page parameter in URL (for back button functionality)
  const urlParams = new URLSearchParams(window.location.search);
  const pageParam = urlParams.get('page');
  
  if (pageParam) {
    // If page parameter exists in URL, use it
    currentPage = parseInt(pageParam, 10);
    minLoadedPage = parseInt(pageParam, 10);
    // Clear localStorage since we're using URL parameter
    localStorage.removeItem('selectedItemPage');
    localStorage.removeItem('selectedItemCollection');
  } else {
    // Otherwise check localStorage (only if it's for the same collection)
    let selectedItemPage = localStorage.getItem('selectedItemPage');
    let selectedItemCollection = localStorage.getItem('selectedItemCollection');
    
    // Only use stored page if it's for the same collection
    if(selectedItemPage && selectedItemCollection === currentCollectionUrl) {
      currentPage = parseInt(selectedItemPage, 10);
      minLoadedPage = parseInt(selectedItemPage, 10);
      localStorage.removeItem('selectedItemPage');
      localStorage.removeItem('selectedItemCollection');
    } else {
      // Clear localStorage if it's for a different collection
      if(selectedItemPage) {
        localStorage.removeItem('selectedItemPage');
        localStorage.removeItem('selectedItemCollection');
        localStorage.removeItem('selectedItemId');
      }
    }
  }

  // Prefetch next page in the background
  const prefetchNextPage = () => {
    // currentPage already represents the next page to load, so prefetch it
    const nextPage = currentPage;
    if ((nextPage - 1) * productLimit >= productTotal || pageCache[nextPage] || isLoading) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    url.searchParams.set('section_id', collectionSectionId);
    
    fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      priority: 'low'
    })
      .then(response => {
        // Section rendering API returns JSON, regular fetch returns HTML
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return response.json();
        }
        return response.text();
      })
      .then(data => {
        let html;
        // Handle section rendering API response (JSON)
        if (typeof data === 'object' && data.sections && collectionSectionId) {
          html = data.sections[collectionSectionId];
        } else {
          // Handle regular HTML response
          html = data;
        }
        
        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newProducts = doc.getElementById('ProductGrid')?.innerHTML;
          if (newProducts) {
            pageCache[nextPage] = newProducts;
          }
        }
      })
      .catch(() => {});
  };

  const loadNewProducts = (direction = 'next') => {
    // If already loading, exit early
    if (isLoading) {
      return;
    }

    // Calculate the page we're about to load
    const pageAboutToLoad = direction === 'next' ? currentPage : minLoadedPage - 1;
    
    // Check if we've already loaded all products (pages before the one we're about to load cover all products)
    const alreadyLoadedAllProducts = (pageAboutToLoad - 1) * productLimit >= productTotal;
    
    if (direction === 'next' && alreadyLoadedAllProducts){
      function checkForFunction() {
        if (typeof window.getShopifySectionsAfterCollection === 'function') {
          window.getShopifySectionsAfterCollection('block');
        } else {
          setTimeout(checkForFunction, 100);
        }
      }
      
      checkForFunction();
    }

    // Don't load if we're at the first page or last page
    if ((direction === 'next' && alreadyLoadedAllProducts) || 
        (direction === 'prev' && minLoadedPage <= 1)) {
      // If this is the first load and we're at the last page, clean up the loader
      if (isFirstLoad && direction === 'next' && alreadyLoadedAllProducts) {
        // Remove hide-initial-products class
        productGrid.closest('.grid-outer')?.classList.remove('hide-initial-products');
        
        isFirstLoad = false;
        
        // Call the function to load sections after collection if available
        function checkForFunction() {
          if (typeof window.getShopifySectionsAfterCollection === 'function') {
            window.getShopifySectionsAfterCollection('block');
          } else {
            setTimeout(checkForFunction, 100);
          }
        }
        checkForFunction();
      }
      return;
    }

    // Set loading flag
    isLoading = true;
    
    // Don't show loader for additional pages - products load quickly and loader causes INP delays
    let pageToLoad = direction === 'next' ? currentPage : minLoadedPage - 1;
    
    // Check cache first
    if (pageCache[pageToLoad]) {
      processProducts(pageCache[pageToLoad], direction, pageToLoad);
      return;
    }

    // If not in cache, fetch from server
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageToLoad);
    if (collectionSectionId) {
      url.searchParams.set('section_id', collectionSectionId);
    }
    
    fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      priority: 'high'
    })
      .then(response => {
        // Section rendering API returns JSON, regular fetch returns HTML
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return response.json();
        }
        return response.text();
      })
      .then(data => {
        let html;
        // Handle section rendering API response (JSON)
        if (typeof data === 'object' && data.sections && collectionSectionId) {
          html = data.sections[collectionSectionId];
        } else {
          // Handle regular HTML response
          html = data;
        }
        
        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          doc.querySelector('.grid-outer')?.classList.remove('hide-initial-products');
          const newProducts = doc.getElementById('ProductGrid')?.innerHTML;
          
          if (newProducts) {
            pageCache[pageToLoad] = newProducts;
          }
          
          processProducts(newProducts, direction, pageToLoad);
        } else {
          isLoading = false;
        }
      })
      .catch(error => {
        console.error(`Error fetching page ${pageToLoad}:`, error);
        // Reset loading flag if there's an error
        isLoading = false;
        
        if (isFirstLoad) {
          // Remove hide-initial-products class
          productGrid.closest('.grid-outer')?.classList.remove('hide-initial-products');
          isFirstLoad = false;
        }
      });
  }

  // Helper function to process products (used by both cache and fetch)
  const processProducts = (newProducts, direction, pageToLoad) => {
    if (isFirstLoad) {
      productGrid.innerHTML = '';
      productGrid.closest('.grid-outer').classList.remove('hide-initial-products');
      
      isFirstLoad = false;
    }
    
    if (newProducts) {
      if (direction === 'next') {
        productGrid.insertAdjacentHTML('beforeend', newProducts);
        currentPage++;
        setTimeout(() => prefetchNextPage(), 100);
      } else {
        // Only adjust scroll position if user is scrolling up (loading previous page)
        const scrollPos = window.scrollY;
        const oldHeight = productGrid.scrollHeight;
        productGrid.insertAdjacentHTML('afterbegin', newProducts);
        const heightDiff = productGrid.scrollHeight - oldHeight;
        // Use requestAnimationFrame to ensure smooth scroll adjustment
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPos + heightDiff);
        });
        minLoadedPage--;
      }
      
      // Only scroll to selected item if it exists (back button scenario)
      // Don't scroll if user is actively scrolling
      if (!isUserScrolling) {
        scrollToSelectedItem();
      }
      disableProductLinks();
      isLoading = false;
    } else {
      // If no products were found, update the limits to prevent future attempts in this direction
      if (direction === 'next') {
        // Set productTotal to current products count to prevent future "next" calls
        productTotal = (currentPage - 1) * productLimit;
      } else if (direction === 'prev') {
        // Set minLoadedPage to 1 to prevent future "prev" calls
        minLoadedPage = 1;
      }
      
      // Reset loading flag
      isLoading = false;
    }
  }

  // If we have a page parameter, load that specific page
  if (pageParam) {
    const targetPage = parseInt(pageParam, 10);
    isLoading = true;
    const url = new URL(window.location.href);
    url.searchParams.set('page', targetPage);
    if (collectionSectionId) {
      url.searchParams.set('section_id', collectionSectionId);
    }
    
    fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      priority: 'high'
    })
      .then(response => {
        // Section rendering API returns JSON, regular fetch returns HTML
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return response.json();
        }
        return response.text();
      })
      .then(data => {
        let html;
        // Handle section rendering API response (JSON)
        if (typeof data === 'object' && data.sections && collectionSectionId) {
          html = data.sections[collectionSectionId];
        } else {
          // Handle regular HTML response
          html = data;
        }
        
        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          doc.querySelector('.grid-outer')?.classList.remove('hide-initial-products');
          const newProducts = doc.getElementById('ProductGrid')?.innerHTML;
          
          if (newProducts) {
            pageCache[targetPage] = newProducts;
            
            if (isFirstLoad) {
              productGrid.innerHTML = '';
              productGrid.closest('.grid-outer').classList.remove('hide-initial-products');
              
              isFirstLoad = false;
            }
            
            productGrid.insertAdjacentHTML('beforeend', newProducts);
            currentPage = targetPage + 1;  // Next page to load after targetPage
            minLoadedPage = targetPage;     // First page loaded
            
            scrollToSelectedItem();
            disableProductLinks();
            isLoading = false;
            
            setTimeout(() => prefetchNextPage(), 100);
          } else {
            isLoading = false;
            loadNewProducts('next');
          }
        } else {
          // No HTML content received
          isLoading = false;
          loadNewProducts('next');
        }
      })
      .catch(error => {
        console.error(`Error loading page ${targetPage}:`, error);
        isLoading = false;
        loadNewProducts('next');
      });
  } else {
    // Normal initial load
    // Check if we're already at the last page before attempting to load
    if (currentPage * productLimit >= productTotal) {
      // No more products to load
      // Remove hide-initial-products class
      productGrid.closest('.grid-outer')?.classList.remove('hide-initial-products');
      
      isFirstLoad = false;
      
      // Call the function to load sections after collection if available
      function checkForFunction() {
        if (typeof window.getShopifySectionsAfterCollection === 'function') {
          window.getShopifySectionsAfterCollection('block');
        } else {
          setTimeout(checkForFunction, 100);
        }
      }
      checkForFunction();
    } else {
      loadNewProducts('next');
      // Prefetch next page after initial load completes
      setTimeout(() => prefetchNextPage(), 500);
    }
  }

  const scrollToSelectedItem = () => {
    const selectedItemId = localStorage.getItem('selectedItemId');
    if(selectedItemId && !isUserScrolling) {
      const selectedItemElement = document.getElementById(selectedItemId);
      if(selectedItemElement) {
        // Use requestAnimationFrame to ensure it doesn't interfere with user scrolling
        requestAnimationFrame(() => {
          // Account for sticky header when scrolling to item
          const elementPosition = selectedItemElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - 46,
            behavior: 'smooth'
          });
        });
                
        // Add highlight class to selected element
        selectedItemElement.closest('.productGridItem').classList.add('highlighted-item');
        // Remove highlight after animation
        setTimeout(() => {
          selectedItemElement.closest('.productGridItem').classList.remove('highlighted-item');
        }, 2000);
      }
      localStorage.removeItem('selectedItemId');
    }
  }
  
  // Detect when scroll is near top to load previous page
  let lastScrollY = window.scrollY;
  
  // Detect scroll direction and load content accordingly
  const handleScroll = () => {
    const scrollDirection = window.scrollY < lastScrollY ? 'up' : 'down';
    lastScrollY = window.scrollY;
    
    // Mark that user is actively scrolling
    isUserScrolling = true;
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      isUserScrolling = false;
    }, 150);

    // Get product dimensions for calculations
    const productItems = productGrid.querySelectorAll('.productGridItem');
    if (!productItems.length) return;

    const itemHeight = productItems[0].offsetHeight;
    const gridComputedStyle = window.getComputedStyle(productGrid);
    const gridGap = parseInt(gridComputedStyle.rowGap || 0, 10);
    const rowHeight = itemHeight + gridGap;

    // Calculate grid layout info
    const productGridWidth = productGrid.offsetWidth;
    const itemWidth = productItems[0].offsetWidth;
    const columnGap = parseInt(gridComputedStyle.columnGap || 0, 10);
    const columnsPerRow = Math.floor((productGridWidth + columnGap) / (itemWidth + columnGap));

    // Calculate the height of two rows
    const twoRowsHeight = rowHeight * 8;

    if (scrollDirection === 'down') {
      // Check if scroll position is near the "two rows remaining" threshold
      const scrollPosition = window.scrollY + window.innerHeight;
      const productGridBottom = productGrid.offsetTop + productGrid.offsetHeight;

      if (scrollPosition >= productGridBottom - twoRowsHeight) {
        loadNewProducts('next');
      }
    } else if (scrollDirection === 'up') {
      // Load previous page when scrolling near the top of the grid
      const scrollPositionFromTop = window.scrollY;
      const productGridTop = productGrid.offsetTop;
      
      if (scrollPositionFromTop <= productGridTop + twoRowsHeight && minLoadedPage > 1) {
        loadNewProducts('prev');
      }
    }
  };

  // Add scroll event listener
  window.addEventListener('scroll', handleScroll);

  const disableProductLinks = () => {
    const productGridItems = document.querySelectorAll('.productGridItem');
  
    productGridItems.forEach(item => {
      item.addEventListener('click', (e) => {
        // Get the page number for this product
        const pageElement = item.querySelector('[data-current-page]');
        const productId = item.getAttribute('id');
        
        if (pageElement) {
          const selectedItemPage = parseInt(pageElement.getAttribute('data-current-page'), 10);
          
          // Store in localStorage with collection URL to verify it's the same collection
          localStorage.setItem('selectedItemPage', selectedItemPage.toString());
          localStorage.setItem('selectedItemCollection', window.location.pathname.split('?')[0]);
          if (productId) {
            localStorage.setItem('selectedItemId', productId);
          }
          
          // Update URL to include page parameter before navigating
          const url = new URL(window.location.href);
          url.searchParams.set('page', selectedItemPage);
          window.history.replaceState({}, '', url.toString());
        }
      });
    });
  }

  // Function to handle when filters are applied
  const handleFiltersApplied = () => {
    // Reset loading flag to allow new loads
    isLoading = false;
    
    // Clear cache when filters are applied (products will be different)
    pageCache = {};
    
    // Clear localStorage when filters are applied (different collection context)
    localStorage.removeItem('selectedItemPage');
    localStorage.removeItem('selectedItemCollection');
    localStorage.removeItem('selectedItemId');

    document.querySelectorAll('.grid-outer').forEach(gridOuter => {
      gridOuter.classList.remove('hide-initial-products');
    });
    
    // Wait for filter system to update DOM, then refresh all data and reset state
    setTimeout(() => {
      // Refresh product grid reference in case it was replaced
      const refreshedProductGrid = document.getElementById('ProductGrid');
      if (refreshedProductGrid) {
        productGrid = refreshedProductGrid;
      }
      
      // Refresh product grid data attributes from the updated DOM
      const pageElement = document.querySelector('.collection-products [data-current-page]');
      const totalElement = document.querySelector('.collection-products [data-product-total]');
      const limitElement = document.querySelector('.collection-products [data-product-limit]');
      const sectionElement = document.querySelector('.collection-products [data-collection-section-id]');
      
      const updatedCurrentPage = pageElement ? parseInt(pageElement.dataset.currentPage, 10) : null;
      const updatedProductTotal = totalElement ? parseInt(totalElement.dataset.productTotal, 10) : null;
      const updatedProductLimit = limitElement ? parseInt(limitElement.dataset.productLimit, 10) : null;
      const updatedCollectionSectionId = sectionElement ? sectionElement.dataset.collectionSectionId : null;
      
      // Update the variables with fresh data from DOM (only if valid)
      let displayedPage = updatedCurrentPage;
      
      if (updatedProductTotal !== null && !isNaN(updatedProductTotal)) {
        productTotal = updatedProductTotal;
      }
      if (updatedProductLimit !== null && !isNaN(updatedProductLimit)) {
        productLimit = updatedProductLimit;
      }
      if (updatedCollectionSectionId) {
        collectionSectionId = updatedCollectionSectionId;
      }
      
      // Reset page tracking - the filtered page is already displayed in DOM
      // So minLoadedPage should be the displayed page, and currentPage should be the next page to load
      if (displayedPage !== null && !isNaN(displayedPage)) {
        minLoadedPage = displayedPage;  // The page currently displayed
        currentPage = displayedPage + 1;  // Next page to load (since displayed page is already loaded)
      }
      
      // Reset isFirstLoad - filters are not initial load
      isFirstLoad = false;
      
      // Ensure isLoading is false to allow new loads
      isLoading = false;
      
      // Scroll to top of product grid
      if (productGrid) {
        window.scrollTo({
          top: productGrid.offsetTop - 100,
          behavior: 'smooth'
        });
        
        // Prefetch next page if there are more products
        if ((currentPage - 1) * productLimit < productTotal) {
          setTimeout(() => prefetchNextPage(), 500);
        }
      }
    }, 200);
  };

  // Listen for the filters_applied event
  window.addEventListener('filters_applied', handleFiltersApplied);
})();
